/**
 * Firmendaten des Verkaeufers (Plan API 2026-11-01/01, E14/E15) — `sellerProfile.get` / `sellerProfile.update`.
 *
 * 🔴 **Schmaler, lokal typisierter Adapter.** Der Router entsteht parallel im Backend (Schritt 3) und steht im
 * veroeffentlichten `AppRouter` von `@xhubio-saas/saas-invoice-api-backend` (3.14.4) noch nicht. Bis die App die
 * Version mit dem Router anhebt, ruft dieser Adapter die beiden Prozeduren ueber den echten tRPC-Client der App
 * (`utils.client`, derselbe Transport, dieselben Cookies) und beschreibt sie mit einer eigenen Schnittstelle —
 * die Antwort wird mit zod geprueft, kein `any` an der Grenze. Danach: `trpc.sellerProfile.get.useQuery()` und
 * `trpc.sellerProfile.update.useMutation()` einsetzen und diese Datei loeschen; der Abfrage-Schluessel unten ist
 * bereits der, den tRPC selbst vergibt, die Entwertung bleibt also dieselbe.
 *
 * Vertrag (Backend `src/seller-profile/router.ts`, Lane `lane/ia-pdf-extract`): `get` → `{ profile, isStored, missing }`
 * — `profile` ist bei `isStored: false` die Vorbelegung des Servers, die Console nimmt dann `null` (Baseline leer, eigene
 * Vorbelegung aus `organization.current`); `update` nimmt den ganzen Block und antwortet `{ profile, missing }`.
 * 🔴 Gemessen 2026-11-01 im Live-Beleg: Der Adapter las die Huelle als Block — `looseObject` mit lauter `nullish`-Feldern
 * nahm sie still an, das Formular blieb nach dem Speichern leer und „E-Rechnung aus PDF" meldete fehlende Firmendaten.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { trpc } from '@/trpc'

/**
 * Obergrenzen je Feld — EINE Stelle fuer Formular-`maxLength` und Pruefung (`textfeld-obergrenzen.md`).
 * Hausprazedenz, weil das `seller`-Schema der API (`invoice-openapi.ts`, `invoicePartySchema`) fuer diese Felder
 * keine `.max()` fuehrt (Warnphase `FIELD_TOO_LONG`): Name/`legalName` und Strasse 200, Ort 100, PLZ 20, Land 2
 * (ISO 3166-1 alpha-2), Steuer-/USt-Nummern 30, IBAN 34 (ISO 13616), BIC 11 (ISO 9362), Bankname und
 * Kontaktname 100, Telefon 50, E-Mail 254 (RFC 5321). `metadata` ist JSON — dort ist die Zod-Grenze des Routers
 * die einzige Verteidigungslinie; sie muss dieselben Zahlen tragen.
 */
export const SELLER_PROFILE_LIMITS = {
  name: 200,
  street: 200,
  postalCode: 20,
  city: 100,
  countryCode: 2,
  vatId: 30,
  taxId: 30,
  iban: 34,
  bic: 11,
  bankName: 100,
  contactName: 100,
  contactPhone: 50,
  contactEmail: 254,
} as const

const text = z.string().nullish()

const sellerProfileSchema = z.looseObject({
  name: text,
  street: text,
  postalCode: text,
  city: text,
  countryCode: text,
  vatId: text,
  taxId: text,
  bankAccount: z.looseObject({ iban: text, bic: text, bankName: text }).nullish(),
  contact: z.looseObject({ name: text, phone: text, email: text }).nullish(),
})

export type SellerProfile = z.infer<typeof sellerProfileSchema>

/**
 * Die beiden Prozeduren, wie dieser Adapter sie erwartet — die Ein- und Ausgabe ist `unknown`, bis zod sie prueft.
 * (Ersetzt durch den inferierten `AppRouter`, sobald das Backend-Paket den Router fuehrt.)
 */
interface SellerProfileProcedures {
  sellerProfile: {
    get: { query: () => Promise<unknown> }
    update: { mutate: (input: SellerProfile) => Promise<unknown> }
  }
}

/** Derselbe Schluessel, den `trpc.sellerProfile.get.useQuery()` vergaebe. */
export const SELLER_PROFILE_QUERY_KEY = [['sellerProfile', 'get'], { type: 'query' }] as const

const getResponseSchema = z.object({
  profile: sellerProfileSchema.nullable(),
  isStored: z.boolean(),
})

const updateResponseSchema = z.object({ profile: sellerProfileSchema })

/** Antwort von `sellerProfile.get` → der gespeicherte Block, `null` solange nie gespeichert (`isStored: false`). */
export function parseGetResponse(raw: unknown): SellerProfile | null {
  const { profile, isStored } = getResponseSchema.parse(raw)
  return isStored ? profile : null
}

/** Antwort von `sellerProfile.update` → der gespeicherte Block. */
export function parseUpdateResponse(raw: unknown): SellerProfile {
  return updateResponseSchema.parse(raw).profile
}

function useProcedures(): SellerProfileProcedures {
  // Der Typ des App-Clients kennt den Router noch nicht (siehe Kopf); zur Laufzeit ist es derselbe Proxy.
  return trpc.useUtils().client as unknown as SellerProfileProcedures
}

export function useSellerProfile() {
  const procedures = useProcedures()
  return useQuery({
    queryKey: SELLER_PROFILE_QUERY_KEY,
    queryFn: async () => parseGetResponse(await procedures.sellerProfile.get.query()),
    retry: false,
  })
}

/** Speichern — 🔴 entwertet `sellerProfile.get` (Hausregel „Wer schreibt, entwertet"). */
export function useSaveSellerProfile() {
  const procedures = useProcedures()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SellerProfile) =>
      parseUpdateResponse(await procedures.sellerProfile.update.mutate(input)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [['sellerProfile', 'get']] })
    },
  })
}

// ─── Vollstaendigkeit (E15) ──────────────────────────────────────────────────────────────────────

/** Pflichtangaben vor dem ersten Auslesen — BR-DE-1 (Bank), BR-DE-2/6/7 (Kontakt), Anschrift, Steuerkennung. */
export type SellerRequirement =
  | 'name'
  | 'street'
  | 'postalCode'
  | 'city'
  | 'countryCode'
  | 'taxIdentifier'
  | 'iban'
  | 'contactPhone'
  | 'contactEmail'

const filled = (v: string | null | undefined): boolean => typeof v === 'string' && v.trim() !== ''

/**
 * Was dem Profil fuer das Auslesen fehlt (leer = vollstaendig). USt-IdNr. **oder** Steuernummer genuegt
 * (Kleinunternehmer, EN 16931 BR-CO-26; E15).
 */
export function missingSellerRequirements(profile: SellerProfile | null): SellerRequirement[] {
  const p = profile ?? {}
  const missing: SellerRequirement[] = []
  if (!filled(p.name)) missing.push('name')
  if (!filled(p.street)) missing.push('street')
  if (!filled(p.postalCode)) missing.push('postalCode')
  if (!filled(p.city)) missing.push('city')
  if (!filled(p.countryCode)) missing.push('countryCode')
  if (!filled(p.vatId) && !filled(p.taxId)) missing.push('taxIdentifier')
  if (!filled(p.bankAccount?.iban)) missing.push('iban')
  if (!filled(p.contact?.phone)) missing.push('contactPhone')
  if (!filled(p.contact?.email)) missing.push('contactEmail')
  return missing
}
